'use strict';

import { buildHelpHTML } from '../../core/util.js';
import { buildSpacingTable, spacingReader, loadSpacingCorpus, SPACING_PASS } from '../space-out.js';
import { SEARCH_CHEAT } from '../assist-search.js';

// ─── Cross-tool helpers ──────────────────────────────────────────────────────
// Only helpers used by 2+ tools belong here; single-use helpers live in their
// tool's own file.

export const MATCH_PARAM = {
  key: 'mode', type: 'match', label: 'Match mode', title: 'Match mode (Alt-W)',
  menuDefault: 'full',
  choices: [
    { value: 'full', label: 'Whole entry' },
    { value: 'start', label: 'Starts entry' },
    { value: 'end', label: 'Ends entry' },
    { value: 'word', label: 'Whole word' },
    { value: 'span', label: 'Spans words' },
  ],
};

export function matchModeOf(params) {
  const v = params && params.mode;
  return MATCH_PARAM.choices.some(c => c.value === v) ? v : '';
}

export function matchModeReadsWords(params) {
  const mode = matchModeOf(params);
  return mode === 'word' || mode === 'span';
}

export const matchModeAssets = params => (matchModeReadsWords(params) ? ['unigrams'] : []);
export const matchModePasses = params => (matchModeReadsWords(params) ? [SPACING_PASS] : []);

// Built up front rather than read on demand: a short pattern's candidates run to
// hundreds of thousands, and reading each on the spot costs seconds per keystroke.
export async function matchModeSpacing(params, ctx) {
  if (!matchModeReadsWords(params)) return null;
  await loadSpacingCorpus();
  return buildSpacingTable(ctx);
}

export const matchModeSpacingLazy = (params, ctx) => (matchModeReadsWords(params) ? spacingReader(ctx) : null);

// `value` (not a boolean checkbox) so the URL reads mode=span like Search and
// Regex — the key can grow into the full mode menu without breaking links.
export const SPAN_PARAM = { key: 'mode', type: 'checkbox', value: 'span', label: 'Spans words', title: 'The match must cross a word break' };
export const ALLOW_UNLISTED_PARAM = { key: 'unlisted', type: 'checkbox', replaceScoped: true, label: 'Allow unlisted', title: "Keep replacements that aren't in the wordlist" };

// `!= null`, not truthiness: an empty replacement is delete mode, and a truthiness "cleanup" silently reverts it to a filter.
export const isReplacing = params => params.replace != null;

export const SEARCH_HELP = buildHelpHTML(SEARCH_CHEAT.map(r => [r.code, r.desc]));

export function looksPlural(word) {
  return word.endsWith('s') && !word.endsWith('ss');
}

export function reverseString(s) {
  let out = '';
  for (let i = s.length - 1; i >= 0; i--) out += s[i];
  return out;
}

// Sort the letters of an already-canonical string. Tools that need letter-bank
// equivalence call this on `entry` (and on user-supplied params, which the
// runtime normalizes the same way before passing in). Non-letters survive and
// participate in the comparison — for letter-only wordlists they're a no-op,
// for the rare punctuation-bearing entry they make the match stricter.
export function sortLetters(s) {
  if (!s) return '';
  return s.split('').sort().join('');
}
